import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useExport from '@apiHooks/useExport';
import { useDebouncedCallback } from '@hooks/useDebouncedCallback';
import { components } from "@api/api";
import { Sort } from "@models";

export type MenuItemExample = components["schemas"]["MenuItemExample"]

type SortParams = {
    direction: Sort.Asc | Sort.Desc;
    column: number;
};

type FetchOptions = {
    sortColumn: number;
    search: string;
    take: number;
    skip: number;
    sortDir: string;
};

type FetchResponse = {
    data: MenuItemExample[];
    totalRecords: number;
};

type AbortablePromise<T = unknown> = Promise<T> & { abort?: () => void };

export const TAKE = 100;
const INITIAL_TOTAL_COUNT = 30;
const DEFAULT_SORT: SortParams = { direction: Sort.Asc, column: 0 };

const createFetchOptions = (search: string, sort: SortParams, skip: number): FetchOptions => ({
    sortColumn: sort.column,
    search,
    take: TAKE,
    skip,
    sortDir: sort.direction,
});

export interface UseMenuExamplesPopupDataParams {
    fetch: (options: FetchOptions) => AbortablePromise<FetchResponse>;
    isShown: boolean;
    getExportData: (options: FetchOptions) => void;
    downloadUrl: string;
}

export const useMenuExamplesPopupData = ({
    fetch,
    isShown,
    getExportData,
    downloadUrl,
}: UseMenuExamplesPopupDataParams) => {
    const [isLoading, setIsLoading] = useState(true);
    const [tableData, setTableData] = useState<MenuItemExample[]>([]);
    const [totalCount, setTotalCount] = useState(INITIAL_TOTAL_COUNT);
    const [sort, setSort] = useState<SortParams>(DEFAULT_SORT);
    const [searchQuery, setSearchQuery] = useState('');

    const requestIdRef = useRef(0);
    const isPopupReadyRef = useRef(false);
    const skipFirstRunRef = useRef({ search: true, sort: true });

    const exportMenuExamples = useExport({
        exportEndpoint: downloadUrl,
        downloadEndpoint: downloadUrl,
    });

    const tableParameters = useMemo(
        () => createFetchOptions(searchQuery, sort, tableData.length),
        [searchQuery, sort, tableData.length]
    );

    const handleSortColumn = useCallback(
        (columnIndex: number) => {
            if (columnIndex === sort.column) {
                setSort(prevState => ({
                    ...prevState,
                    direction: sort.direction === Sort.Asc ? Sort.Desc : Sort.Asc,
                }));
            } else {
                setSort({
                    direction: Sort.Asc,
                    column: columnIndex,
                });
            }
        },
        [sort]
    );

    const handleChangeSearchQuery = useCallback((value: string) => setSearchQuery(value), []);

    const download = useCallback(async () => {
        const exportData = getExportData(tableParameters);
        exportMenuExamples(exportData);
    }, [exportMenuExamples, getExportData, tableParameters]);

    const resetData = useCallback(() => {
        setSearchQuery('');
        setIsLoading(true);
        setTotalCount(INITIAL_TOTAL_COUNT);
        setSort(DEFAULT_SORT);
    }, []);

    const doFetch = useCallback(async (searchVal: string, sortVal: SortParams, skip: number = 0) => {
        if (!isShown) return;

        const id = ++requestIdRef.current;
        setIsLoading(true);

        try {
            const { data, totalRecords } = await fetch(createFetchOptions(searchVal, sortVal, skip));
            if (id !== requestIdRef.current) return;

            setTableData(prev => (skip === 0 ? data : [...prev, ...data]));
            setTotalCount(totalRecords);
        } finally {
            if (id === requestIdRef.current) setIsLoading(false);
        }
    }, [isShown, fetch]);

    const debouncedSearch = useDebouncedCallback((searchVal: string, sortVal: SortParams) => {
        doFetch(searchVal, sortVal, 0);
    }, 150);

    useEffect(() => {
        if (isShown) {
            isPopupReadyRef.current = true;
            doFetch(searchQuery, sort, 0);
        } else {
            isPopupReadyRef.current = false;
            resetData();
        }
    }, [isShown]);

    useEffect(() => {
        if (skipFirstRunRef.current.search) {
            skipFirstRunRef.current.search = false;
            return;
        }
        if (isShown && isPopupReadyRef.current) {
            setTotalCount(INITIAL_TOTAL_COUNT);
            debouncedSearch(searchQuery, sort);
        }
    }, [searchQuery]);

    useEffect(() => {
        if (skipFirstRunRef.current.sort) {
            skipFirstRunRef.current.sort = false;
            return;
        }
        if (isShown && isPopupReadyRef.current) {
            setTotalCount(INITIAL_TOTAL_COUNT);
            doFetch(searchQuery, sort, 0);
        }
    }, [sort]);

    const loadMore = useCallback(() => {
        doFetch(searchQuery, sort, tableData.length);
    }, [doFetch, searchQuery, sort, tableData.length]);

    return {
        download,
        tableData,
        sort,
        isLoading,
        totalCountExamples: totalCount,
        getMenuExamples: loadMore,
        handleSortColumn,
        searchQuery,
        handleChangeSearchQuery,
    };
};
